import { canWriteEntity } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'

/**
 * The Move picker's judgement (#2543; Joni's canvas https://claude.ai/artifact/28JiKUaiCvbKecRqVnRTPe). The strings
 * are the canvas's approved copy, with its typographic quotes and apostrophes.
 */

type Item = Persisted<Entity>

/** Where the picker is: the folders from a space down; empty at the top level (All spaces, whose destination is Unfiled) */
export type Trail = Persisted<Entity>[]

export const unfiledName = 'Unfiled'
export const cantAddReason = 'You can’t add items here'
/** The same, as the footer's sentence beside a disabled Move here (Joni, #2556) */
export const cantAddMoveReason = 'You can’t add items here.'
export const alreadyHereReason = 'It’s already here.'
export const currentFolderTag = 'Current folder'

const nameOf = (entity: Pick<Item, 'name'>) => entity.name || 'Untitled'

/** An item with no parent, or the `'ROOT'` sentinel, is Unfiled */
const parentOf = (item: Item) => (!item.parentId || item.parentId === 'ROOT' ? null : item.parentId)

/** The folder Move here moves to: the one you are in; `null` (Unfiled) at the top level */
export const destinationId = (trail: Trail) => trail.at(-1)?.id ?? null

export const destinationName = (trail: Trail) => {
  const folder = trail.at(-1)
  return folder ? nameOf(folder) : unfiledName
}

/** The footer's `To: {path}`, from the space down */
export const destinationPath = (trail: Trail) => (trail.length > 0 ? trail.map(nameOf).join(' › ') : unfiledName)

/** `All spaces › Space › Folder`; `depth` is how many folders deep the crumb leads */
export const crumbs = (trail: Trail) => [
  { label: 'All spaces', depth: 0 },
  ...trail.map((folder, index) => ({ label: nameOf(folder), depth: index + 1 })),
]

const movable = (items: Item[], userId?: string | null) => items.filter(item => canWriteEntity(userId, item))

/** The items Move here moves: writable, and not already in the destination */
export const itemsToMove = ({ items, trail, userId }: { items: Item[]; trail: Trail; userId?: string | null }) =>
  movable(items, userId).filter(item => parentOf(item) !== destinationId(trail))

export type MoveHereState = { enabled: true; count: number } | { enabled: false; reason: string }

/**
 * Move here: never into a folder I can't add to (#2514's rule), and only for items that are not there already. The
 * count leaves out what is read-only or already here.
 */
export const moveHere = ({ items, trail, userId }: { items: Item[]; trail: Trail; userId?: string | null }) => {
  const folder = trail.at(-1)
  if (folder && !canWriteEntity(userId, folder)) {
    return { enabled: false, reason: cantAddMoveReason } satisfies MoveHereState
  }
  const toMove = itemsToMove({ items, trail, userId })
  if (toMove.length === 0) {
    return { enabled: false, reason: alreadyHereReason } satisfies MoveHereState
  }
  return { enabled: true, count: toMove.length } satisfies MoveHereState
}

/** A folder row: tagged when it holds every item, and the reason when I can't add to it. It can still be opened. */
export const folderRow = ({ folder, items, userId }: { folder: Item; items: Item[]; userId?: string | null }) => ({
  current: items.length > 0 && items.every(item => parentOf(item) === folder.id),
  reason: canWriteEntity(userId, folder) ? undefined : cantAddReason,
})

/** The items being moved are never offered as a destination, so a folder can't go inside itself */
export const isListed = (folder: Item, items: Item[]) => !items.some(item => item.id === folder.id)

export const pickerTitle = (items: Item[]) =>
  items.length === 1 ? `Move “${nameOf(items[0])}”` : `Move ${items.length} items`

/** The button: `Move here` for one item, `Move {k} items here` for several; `Move here` while nothing can move */
export const moveHereLabel = (items: Item[], count?: number) =>
  items.length === 1 || !count ? 'Move here' : `Move ${count} items here`

const listFormat = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' })

/** Several items, some read-only: `{k} of {n} can be moved. “{name}” is read-only.` */
export const readOnlyNotice = (items: Item[], userId?: string | null) => {
  const readOnly = items.filter(item => !canWriteEntity(userId, item))
  if (readOnly.length === 0 || items.length < 2) {
    return undefined
  }
  const names = listFormat.format(readOnly.map(item => `“${nameOf(item)}”`))
  const can = items.length - readOnly.length
  return `${can} of ${items.length} can be moved. ${names} ${readOnly.length === 1 ? 'is' : 'are'} read-only.`
}

export const movedText = (moved: Item[], destination: string) =>
  moved.length === 1
    ? `Moved “${nameOf(moved[0])}” to ${destination}.`
    : `Moved ${moved.length} items to ${destination}.`

export const moveFailedText = (failed: Item[]) =>
  failed.length === 1
    ? `Couldn’t move “${nameOf(failed[0])}”. Nothing changed.`
    : `Couldn’t move ${failed.length} items. Nothing changed.`

/** A search result (#2543 M2, screen C): a folder with its parent chain, nearest parent first, as the index stores it */
export type FolderResult = Persisted<Entity> & { parentChain?: Pick<Persisted<Entity>, 'id' | 'name'>[] }

/** The folders from the space down to the result, itself last */
export const resultTrail = (result: FolderResult) =>
  [...[...(result.parentChain ?? [])].reverse(), result] as Persisted<Entity>[]

/** "The Wildflowers › Archive › Live set 2025": the result's path, itself last */
export const resultPath = (result: FolderResult) => resultTrail(result).map(nameOf).join(' › ')

/** Neither an item being moved nor anything inside one: a folder can't go inside itself */
export const isOfferedResult = (result: FolderResult, items: Item[]) =>
  !resultTrail(result).some(folder => items.some(item => item.id === folder.id))

export const folderCount = (count: number) => `${count} ${count === 1 ? 'folder' : 'folders'}`

export const noFolderMatch = (query: string) => `No folders match “${query}”.`

const recentKept = 10
const recentShownCap = 5

/** Recent (#2543 M2): the folder moved to, newest first; Unfiled is not a folder to remember */
export const rememberRecent = (recent: string[], folderId: string | null) =>
  folderId ? [folderId, ...recent.filter(id => id !== folderId)].slice(0, recentKept) : recent

/** Up to 5 folders I can still add to, never one being moved */
export const recentShown = ({ folders, items, userId }: { folders: Item[]; items: Item[]; userId?: string | null }) =>
  folders.filter(folder => canWriteEntity(userId, folder) && isListed(folder, items)).slice(0, recentShownCap)

/**
 * A failed New folder (Joni, #2568). New copy, pending the user's approval through Brian: Joni's suggestion, as
 * written.
 */
export const newFolderFailedText = (name: string) => `Couldn’t create “${name}”. Try again.`
